import type { WSMessage, WSMessageType } from '@shared/ws';

import { log } from '../logger';

type ExtractMessage<T extends WSMessageType> = Extract<WSMessage, { type: T }>;
type Handler<T extends WSMessageType> = (message: ExtractMessage<T>) => void;

export type SignalingState = 'idle' | 'connecting' | 'open' | 'closed' | 'error';
export type SignalingStateListener = (state: SignalingState) => void;

interface Options {
  url: string;
  /** Maximum number of reconnect attempts. Default 5. */
  maxRetries?: number;
  /** Initial backoff in ms. Default 1000. */
  initialBackoffMs?: number;
  /** Max backoff cap in ms. Default 30000. */
  maxBackoffMs?: number;
  /** Heartbeat interval in ms. Default 25000 (ping). */
  heartbeatMs?: number;
}

/**
 * Browser WebSocket client with auto-reconnect (exponential backoff capped at 30s),
 * heartbeat pings every 25s, and a typed event dispatcher keyed on `WSMessage.type`.
 *
 * Singleton-per-tab via `getSignalingClient(url)`; consumers subscribe to events
 * via `.on(type, handler)`. Zustand store reflects connection state.
 */
export class SignalingClient {
  private ws: WebSocket | null = null;
  private state: SignalingState = 'idle';
  private retries = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closedByCaller = false;
  private readonly handlers = new Map<WSMessageType, Set<Handler<WSMessageType>>>();
  private readonly stateListeners = new Set<SignalingStateListener>();

  constructor(private readonly opts: Options) {}

  connect(): void {
    if (this.state === 'connecting' || this.state === 'open') return;
    this.closedByCaller = false;
    this.openSocket();
  }

  on<T extends WSMessageType>(type: T, handler: Handler<T>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler as unknown as Handler<WSMessageType>);
    return () => set?.delete(handler as unknown as Handler<WSMessageType>);
  }

  onState(listener: SignalingStateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.state);
    return () => this.stateListeners.delete(listener);
  }

  send(message: WSMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      log.warn('signaling_send_not_open', { type: message.type });
      return false;
    }
    this.ws.send(JSON.stringify(message));
    return true;
  }

  close(): void {
    this.closedByCaller = true;
    this.clearTimers();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.setState('closed');
  }

  getState(): SignalingState {
    return this.state;
  }

  private openSocket(): void {
    this.setState('connecting');
    const ws = new WebSocket(this.opts.url);
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.retries = 0;
      this.setState('open');
      this.startHeartbeat();
    });

    ws.addEventListener('message', (ev) => {
      let parsed: WSMessage;
      try {
        parsed = JSON.parse(ev.data as string) as WSMessage;
      } catch {
        log.warn('signaling_parse_error', { data: String(ev.data).slice(0, 100) });
        return;
      }
      const set = this.handlers.get(parsed.type);
      if (!set || set.size === 0) {
        log.info('signaling_unhandled', { type: parsed.type });
        return;
      }
      for (const h of set) {
        try {
          h(parsed);
        } catch (err) {
          log.error('signaling_handler_threw', { err: String(err) });
        }
      }
    });

    ws.addEventListener('error', () => {
      this.setState('error');
    });

    ws.addEventListener('close', () => {
      this.clearTimers();
      this.ws = null;
      if (this.closedByCaller) {
        this.setState('closed');
        return;
      }
      const max = this.opts.maxRetries ?? 5;
      if (this.retries >= max) {
        this.setState('closed');
        return;
      }
      const initial = this.opts.initialBackoffMs ?? 1000;
      const cap = this.opts.maxBackoffMs ?? 30_000;
      const delay = Math.min(initial * 2 ** this.retries, cap);
      this.retries += 1;
      log.warn('signaling_reconnect_scheduled', { retries: this.retries, delay });
      this.reconnectTimer = setTimeout(() => this.openSocket(), delay);
    });
  }

  private startHeartbeat(): void {
    this.clearTimers();
    const interval = this.opts.heartbeatMs ?? 25_000;
    this.heartbeatTimer = setInterval(() => this.send({ type: 'ping' }), interval);
  }

  private clearTimers(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.heartbeatTimer = null;
    this.reconnectTimer = null;
  }

  private setState(next: SignalingState): void {
    if (this.state === next) return;
    this.state = next;
    for (const listener of this.stateListeners) {
      listener(next);
    }
  }
}

let singleton: SignalingClient | null = null;

export function getSignalingClient(url: string): SignalingClient {
  if (!singleton) {
    singleton = new SignalingClient({ url });
  }
  return singleton;
}

export function destroySignalingClient(): void {
  if (singleton) {
    singleton.close();
    singleton = null;
  }
}
